import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './index.module.scss'
import {
    InlineImageUploadHandler,
    SimpleEditor,
} from "../../../../../TiptapEditor/components/tiptap-templates/simple/simple-editor";
import { isDescriptionTooLong } from '../../utils/descriptionValidation';
import EditIcon from '../../../../../../assets/icon/EditIcon';

const OPENABLE_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:'])

interface DescriptionProps {
    saveDescription: (description: string) => void
    onDraftChange: (description: string) => void
    onValidationChange: (hasError: boolean) => void
    description: string
    maxLength: number
    errorMessage: string
    serverError?: string | null
    // Whether the surrounding modal is currently open. Closing it drops the
    // component back to read mode without remounting the editor.
    isActive?: boolean
    onInlineImageUpload: InlineImageUploadHandler
    onInlineImageError: (error: Error) => void
}

export default function Description({
    saveDescription,
    onDraftChange,
    onValidationChange,
    description,
    maxLength,
    isActive = true,
    errorMessage,
    serverError,
    onInlineImageUpload,
    onInlineImageError,
}: DescriptionProps){
    const [isEditing, setIsEditing] = useState(false)
    const [descriptionLength, setDescriptionLength] = useState(description?.length || 0)
    const hasLengthError = descriptionLength > maxLength
    const hasError = hasLengthError || Boolean(serverError)
    const descriptionRef = useRef<HTMLDivElement>(null)
    const currentDescriptionRef = useRef(description || '')
    const previousErrorStateRef = useRef(hasLengthError)

    useEffect(() => {
        const nextLength = description?.length || 0
        const nextHasError = isDescriptionTooLong(description)

        setDescriptionLength(nextLength)
        currentDescriptionRef.current = description || ''
        previousErrorStateRef.current = nextHasError
        onDraftChange(description || '')
        onValidationChange(nextHasError)
    }, [description, onDraftChange, onValidationChange])

    useEffect(() => {
        if (!isActive) {
            setIsEditing(false)
        }
    }, [isActive])

    const handleDescriptionChange = useCallback((nextDescription: string) => {
        const nextHasError = isDescriptionTooLong(nextDescription)

        currentDescriptionRef.current = nextDescription
        setDescriptionLength(nextDescription.length)
        onDraftChange(nextDescription)

        if (previousErrorStateRef.current !== nextHasError) {
            previousErrorStateRef.current = nextHasError
            onValidationChange(nextHasError)
        }
    }, [onDraftChange, onValidationChange])

    useEffect(() => {
        if (!isEditing) {
            return
        }

        const handleOutsidePointerDown = (event: PointerEvent) => {
            const target = event.target
            if (!(target instanceof Node) || descriptionRef.current?.contains(target)) {
                return
            }

            if (
                target instanceof Element
                && target.closest('[data-slot^="tiptap-"], [data-radix-popper-content-wrapper]')
            ) {
                return
            }

            saveDescription(currentDescriptionRef.current)
            setIsEditing(false)
        }

        document.addEventListener('pointerdown', handleOutsidePointerDown)
        return () => document.removeEventListener('pointerdown', handleOutsidePointerDown)
    }, [isEditing, saveDescription])

    // In read mode a click on a link opens it; any other click starts editing.
    const handleReadModeClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const link = event.target instanceof Element
            ? event.target.closest<HTMLAnchorElement>('a[href]')
            : null

        if (!link) {
            setIsEditing(true)
            return
        }

        event.preventDefault()
        if (OPENABLE_LINK_PROTOCOLS.has(link.protocol)) {
            window.open(link.href, '_blank', 'noopener,noreferrer')
        }
    }

    return (
        <div className={styles.description} ref={descriptionRef}>
            <div className={`${styles.description__title} ${isEditing ? styles.description__titleEditing : ''}`}>
                <span>Описание</span>
                {isEditing && hasError ? (
                    <span className={`${styles.description__counter} ${styles.description__counterError}`}>
                        {descriptionLength}/{maxLength}
                    </span>
                ) : null}
                {!isEditing ? (
                    <button
                        aria-label="Редактировать описание"
                        className={styles.description__editBtn}
                        onClick={() => setIsEditing(true)}
                        type="button"
                    >
                        <EditIcon />
                    </button>
                ) : null}
            </div>
            {isEditing ? (
                <div className={`${styles.description__input} ${hasError ? styles.description__inputError : ''}`}>
                    <SimpleEditor
                        autoFocus
                        editable
                        html={description}
                        onChange={handleDescriptionChange}
                        saveEditorData={saveDescription}
                        showToolbar
                        onInlineImageUpload={onInlineImageUpload}
                        onInlineImageError={onInlineImageError}
                    />
                </div>
            ) : description ? (
                <div className={styles.description__text} onClick={handleReadModeClick}>
                    <SimpleEditor
                        editable={false}
                        html={description}
                        showToolbar={false}
                    />
                </div>
            ) : (
                <div className={styles.description__placeholder} onClick={() => setIsEditing(true)}>
                    Добавить описание
                </div>
            )}
            {hasError && (
                <div className={styles.description__error} role="alert">
                    {serverError || errorMessage}
                </div>
            )}
        </div>
    )
}
